import { computed, type Ref } from 'vue'

import DownloadIcon from '../../../../assets/icons/download.svg?component'
import { ContentItemStatusActionEnum, ContentItemStatusEnum } from '../../../../types'
import { isThumbnailOnlyVideo, resolveDownloadableAssetUrl } from '../../../../utils'
import type {
  ActionButton,
  ContentItem,
  ContentItemDownloadRequest,
  FooterButtonAction,
  ViewMode,
} from '../content-item-details.types'

export type UseContentItemActionsOptions = {
  activeContentItem: Ref<ContentItem | undefined>
  viewMode: () => ViewMode
  loadingAction: () => ContentItemStatusActionEnum | null | undefined
  isDownloading: () => boolean | undefined
  onApprove: (contentItemId: string) => void
  onReject: (contentItemId: string) => void
  onDownload: (request: ContentItemDownloadRequest) => void
}

export const useContentItemActions = ({
  activeContentItem,
  viewMode,
  loadingAction,
  isDownloading,
  onApprove,
  onReject,
  onDownload,
}: UseContentItemActionsOptions) => {
  const activeStatus = computed(
    () => activeContentItem.value?.status as ContentItemStatusEnum | undefined,
  )

  const approve = () => {
    if (
      loadingAction() ||
      !activeContentItem.value ||
      activeStatus.value === ContentItemStatusEnum.Approved
    ) {
      return
    }
    onApprove(String(activeContentItem.value.id))
  }

  const reject = () => {
    if (
      loadingAction() ||
      !activeContentItem.value ||
      activeStatus.value === ContentItemStatusEnum.Rejected
    ) {
      return
    }
    onReject(String(activeContentItem.value.id))
  }

  const download = () => {
    if (!activeContentItem.value) {
      return
    }
    const asset = activeContentItem.value.assets[0]!
    const url = resolveDownloadableAssetUrl(asset)
    if (!url) {
      return
    }
    onDownload({
      contentItemId: String(activeContentItem.value.id),
      assetId: asset.type === 'video' && !isThumbnailOnlyVideo(asset) ? asset.id : undefined,
      url,
    })
  }

  const handleAction = (action: FooterButtonAction) => {
    if (action === ContentItemStatusActionEnum.Approve) {
      approve()
    } else if (action === ContentItemStatusActionEnum.Reject) {
      reject()
    } else {
      download()
    }
  }

  const actionButtons = computed<ActionButton[]>(() => {
    const status = activeStatus.value
    return [
      {
        action: ContentItemStatusActionEnum.Approve,
        hotkey: 'A',
        icon: null,
        label: status === ContentItemStatusEnum.Approved ? 'Approved' : 'Approve',
        isLoading: loadingAction() === ContentItemStatusActionEnum.Approve,
        cssClass:
          status === ContentItemStatusEnum.Approved ? 'content-item-details__button--approved' : '',
        adminOnly: true,
      },
      {
        action: ContentItemStatusActionEnum.Reject,
        hotkey: 'R',
        icon: null,
        label: status === ContentItemStatusEnum.Rejected ? 'Rejected' : 'Reject',
        isLoading: loadingAction() === ContentItemStatusActionEnum.Reject,
        cssClass:
          status === ContentItemStatusEnum.Rejected ? 'content-item-details__button--rejected' : '',
        adminOnly: true,
      },
      {
        action: 'download' as FooterButtonAction,
        hotkey: '',
        icon: DownloadIcon,
        label: 'Download',
        isLoading: isDownloading() ?? false,
        cssClass: '',
        adminOnly: false,
      },
    ].filter((button) => !button.adminOnly || viewMode() === 'admin')
  })

  return { approve, reject, download, handleAction, actionButtons, activeStatus }
}
